import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { getInvoiceById, sendInvoice, createCreditNote, formatKES } from '../../../services/invoiceService';
import { generateInvoicePDF } from '../../../utils/generateInvoicePDF';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-secondary-100 text-secondary-700' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700' },
  partially_paid: { label: 'Partially Paid', color: 'bg-amber-100 text-amber-700' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', color: 'bg-secondary-100 text-secondary-500' }
};

const InvoiceDetailModal = ({ invoiceId, onClose, onUpdated }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [showCreditNote, setShowCreditNote] = useState(false);
  const [creditForm, setCreditForm] = useState({ amount: '', reason: '' });
  const [creditSaving, setCreditSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('invoice');

  useEffect(() => {
    loadInvoice();
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const data = await getInvoiceById(invoiceId);
      setInvoice(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      setSending(true);
      await sendInvoice(invoiceId);
      await loadInvoice();
      onUpdated?.();
    } catch (err) {
      setError(err?.message);
    } finally {
      setSending(false);
    }
  };

  const handleCreditNote = async () => {
    if (!creditForm?.amount || !creditForm?.reason) return;
    try {
      setCreditSaving(true);
      await createCreditNote(invoiceId, creditForm);
      await loadInvoice();
      setShowCreditNote(false);
      setCreditForm({ amount: '', reason: '' });
      onUpdated?.();
    } catch (err) {
      setError(err?.message);
    } finally {
      setCreditSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-surface rounded-2xl p-8 flex items-center gap-3">
          <Icon name="Loader" size={20} className="animate-spin text-primary" />
          <span className="text-sm text-text-secondary">Loading invoice...</span>
        </div>
      </div>
    );
  }

  if (!invoice) return null;

  const statusCfg = STATUS_CONFIG?.[invoice?.status] || STATUS_CONFIG?.draft;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-heading-semibold text-text-primary">{invoice?.invoiceNumber}</h2>
            <span className={`px-2 py-0.5 rounded-full text-xs font-body-medium ${statusCfg?.color}`}>
              {statusCfg?.label}
            </span>
            {invoice?.isLocked && <Icon name="Lock" size={14} className="text-text-secondary" />}
          </div>
          <div className="flex items-center gap-2">
            {invoice?.status === 'draft' && (
              <button
                onClick={handleSend}
                disabled={sending}
                className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-body-medium hover:bg-primary-700 transition-colors"
              >
                {sending ? <Icon name="Loader" size={13} className="animate-spin" /> : <Icon name="Send" size={13} />}
                Send Invoice
              </button>
            )}
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg text-xs font-body-medium text-text-secondary hover:text-text-primary transition-colors"
            >
              <Icon name="Printer" size={13} />
              Print / PDF
            </button>
            <button
              onClick={() => generateInvoicePDF(invoice)}
              className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-body-medium hover:bg-emerald-700 transition-colors"
            >
              <Icon name="Download" size={13} />
              Download PDF
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-secondary-50 transition-colors">
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-3 border-b border-border">
          {['invoice', 'payments', 'credit_notes']?.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-body-medium rounded-t-lg transition-colors ${
                activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {tab === 'invoice' ? 'Invoice' : tab === 'payments' ? `Payments (${invoice?.payments?.length || 0})` : `Credit Notes (${invoice?.creditNotes?.length || 0})`}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 px-4 py-3 rounded-lg text-sm">
              <Icon name="AlertCircle" size={16} />{error}
            </div>
          )}

          {/* Invoice Tab */}
          {activeTab === 'invoice' && (
            <div className="space-y-6">
              {/* Firm Header */}
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                      <Icon name="Scale" size={16} color="white" />
                    </div>
                    <span className="text-xl font-heading-semibold text-primary">LawDesk KE</span>
                  </div>
                  <p className="text-xs text-text-secondary">Legal Practice Management</p>
                  <p className="text-xs text-text-secondary">Nairobi, Kenya | PIN: P051234567X</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-heading-semibold text-text-primary">INVOICE</p>
                  <p className="text-sm font-body-medium text-primary">{invoice?.invoiceNumber}</p>
                  <p className="text-xs text-text-secondary mt-1">Issue: {invoice?.issueDate ? new Date(invoice.issueDate)?.toLocaleDateString('en-KE') : '—'}</p>
                  <p className="text-xs text-text-secondary">Due: {invoice?.dueDate ? new Date(invoice.dueDate)?.toLocaleDateString('en-KE') : '—'}</p>
                </div>
              </div>

              {/* Bill To */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-secondary-50 rounded-xl p-4">
                  <p className="text-xs font-body-medium text-text-secondary mb-2">BILL TO</p>
                  <p className="text-sm font-heading-semibold text-text-primary">{invoice?.client?.fullName || '—'}</p>
                  <p className="text-xs text-text-secondary">{invoice?.client?.email}</p>
                  {invoice?.client?.phone && <p className="text-xs text-text-secondary">{invoice?.client?.phone}</p>}
                </div>
                <div className="bg-secondary-50 rounded-xl p-4">
                  <p className="text-xs font-body-medium text-text-secondary mb-2">MATTER</p>
                  <p className="text-sm font-heading-semibold text-text-primary">{invoice?.caseTitle || '—'}</p>
                  {invoice?.description && <p className="text-xs text-text-secondary mt-1">{invoice?.description}</p>}
                </div>
              </div>

              {/* Line Items */}
              <div>
                <table className="w-full">
                  <thead>
                    <tr className="bg-secondary-100 rounded-lg">
                      <th className="text-left text-xs font-body-medium text-text-secondary px-3 py-2">Description</th>
                      <th className="text-center text-xs font-body-medium text-text-secondary px-3 py-2">Type</th>
                      <th className="text-right text-xs font-body-medium text-text-secondary px-3 py-2">Qty</th>
                      <th className="text-right text-xs font-body-medium text-text-secondary px-3 py-2">Rate</th>
                      <th className="text-right text-xs font-body-medium text-text-secondary px-3 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(invoice?.lineItems || [])?.map(item => (
                      <tr key={item?.id}>
                        <td className="px-3 py-2.5 text-sm text-text-primary">{item?.description}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="text-xs bg-secondary-100 text-text-secondary px-2 py-0.5 rounded">
                            {item?.itemType === 'time' ? 'Time' : item?.itemType === 'fixed_fee' ? 'Fixed Fee' : 'Disbursement'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-sm text-right text-text-secondary">{item?.quantity}</td>
                        <td className="px-3 py-2.5 text-sm text-right text-text-secondary">{formatKES(item?.rate)}</td>
                        <td className="px-3 py-2.5 text-sm text-right font-body-medium">{formatKES(item?.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Subtotal</span>
                    <span>{formatKES(invoice?.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">VAT ({invoice?.vatRate || 16}%)</span>
                    <span>{formatKES(invoice?.vatAmount)}</span>
                  </div>
                  <div className="flex justify-between text-base font-heading-semibold border-t border-border pt-2">
                    <span>Total (KES)</span>
                    <span className="text-primary">{formatKES(invoice?.total)}</span>
                  </div>
                  {parseFloat(invoice?.paidAmount) > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-emerald-600">
                        <span>Paid</span>
                        <span>- {formatKES(invoice?.paidAmount)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-heading-semibold text-amber-600 border-t border-border pt-2">
                        <span>Balance Due</span>
                        <span>{formatKES(invoice?.remainingBalance)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Notes */}
              {invoice?.notes && (
                <div className="bg-secondary-50 rounded-xl p-4">
                  <p className="text-xs font-body-medium text-text-secondary mb-1">Notes & Payment Terms</p>
                  <p className="text-sm text-text-primary">{invoice?.notes}</p>
                </div>
              )}

              {/* Tax Compliance */}
              <div className="border-t border-border pt-4">
                <p className="text-xs text-text-secondary text-center">
                  This invoice is issued in compliance with Kenya Revenue Authority (KRA) tax regulations.
                  VAT Registration: P051234567X | All amounts in Kenya Shillings (KES)
                </p>
              </div>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div className="space-y-3">
              {invoice?.payments?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="CreditCard" size={32} className="text-text-secondary mx-auto mb-2" />
                  <p className="text-sm text-text-secondary">No payments recorded yet</p>
                </div>
              ) : (
                invoice?.payments?.map(payment => (
                  <div key={payment?.id} className="bg-secondary-50 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Icon name="CheckCircle" size={18} className="text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-body-medium text-text-primary">{formatKES(payment?.amount)}</p>
                        <p className="text-xs text-text-secondary">
                          {payment?.paymentMethod?.toUpperCase()} · {payment?.paymentDate ? new Date(payment.paymentDate)?.toLocaleDateString('en-KE') : '—'}
                        </p>
                        {payment?.referenceNumber && (
                          <p className="text-xs text-text-secondary">Ref: {payment?.referenceNumber}</p>
                        )}
                        {payment?.mpesaTransactionId && (
                          <p className="text-xs text-text-secondary">M-Pesa: {payment?.mpesaTransactionId}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Recorded</span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Credit Notes Tab */}
          {activeTab === 'credit_notes' && (
            <div className="space-y-4">
              {!showCreditNote && (
                <button
                  onClick={() => setShowCreditNote(true)}
                  className="flex items-center gap-2 text-sm text-primary hover:text-primary-700 font-body-medium"
                >
                  <Icon name="Plus" size={16} />
                  Issue Credit Note
                </button>
              )}

              {showCreditNote && (
                <div className="bg-secondary-50 rounded-xl p-4 space-y-3">
                  <h4 className="text-sm font-body-medium text-text-primary">New Credit Note</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">Amount (KES)</label>
                      <input
                        type="number"
                        value={creditForm?.amount}
                        onChange={e => setCreditForm(f => ({ ...f, amount: e?.target?.value }))}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text-secondary mb-1">Reason</label>
                      <input
                        type="text"
                        value={creditForm?.reason}
                        onChange={e => setCreditForm(f => ({ ...f, reason: e?.target?.value }))}
                        placeholder="Reason for credit note"
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreditNote}
                      disabled={creditSaving}
                      className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-body-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      {creditSaving ? 'Saving...' : 'Issue Credit Note'}
                    </button>
                    <button
                      onClick={() => setShowCreditNote(false)}
                      className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {invoice?.creditNotes?.length === 0 && !showCreditNote ? (
                <div className="text-center py-8">
                  <Icon name="FileX" size={32} className="text-text-secondary mx-auto mb-2" />
                  <p className="text-sm text-text-secondary">No credit notes issued</p>
                </div>
              ) : (
                invoice?.creditNotes?.map(cn => (
                  <div key={cn?.id} className="bg-secondary-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-body-medium text-text-primary">{cn?.creditNoteNumber}</p>
                        <p className="text-xs text-text-secondary">{cn?.reason}</p>
                        <p className="text-xs text-text-secondary">{cn?.issuedAt ? new Date(cn.issuedAt)?.toLocaleDateString('en-KE') : '—'}</p>
                      </div>
                      <span className="text-sm font-heading-semibold text-red-600">- {formatKES(cn?.amount)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailModal;
